// Halaman Timeline — layout ala v2 (grup per departemen, toggle minggu/bulan,
// filter orang/departemen, drawer klik-project) dengan tema V1 dan bar ber-% progres.
// Data & mutasi lewat useApp() (Neon via /api). Start project = createdAt, selesai = deadline.

import { useEffect, useMemo, useState, useCallback } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useSession } from '@/context/AuthContext'
import { GanttChartSquare } from 'lucide-react'
import { useApp } from '@/context/AppContext'
import { deptColor } from '@/lib/departments'
import { picColor } from '@/lib/utils'
import { hasCrossDeptView } from '@/lib/roles'
import type { Project, Task, Subtask, ProjectNote } from '@/lib/types'

const DAY = 86400000
const DEPARTMENTS = ['HOLDING', 'HAI-X', 'PADEL PARTY', 'UIM', 'UKP', 'DPP']
const LEFT_W = 240
const ROW_H = 52

function startOf(p: Project): Date { const d = new Date(p.createdAt); d.setHours(0, 0, 0, 0); return d }
function endOf(p: Project): Date | null { if (!p.deadline) return null; const d = new Date(p.deadline); d.setHours(0, 0, 0, 0); return d }
function startOfWeek(d: Date): Date { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x }
const daysInMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
const isoDay = (v: string | null | undefined) => (v ? v.slice(0, 10) : '')
const fmtDay = (d: Date) => d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })

function peopleOf(p: Project, tasks: Task[], subtasks: Subtask[]): string[] {
  const seen = new Map<string, string>()
  const add = (n?: string | null) => { const v = (n ?? '').trim(); if (v && !seen.has(v.toLowerCase())) seen.set(v.toLowerCase(), v) }
  add(p.pic)
  const pts = tasks.filter((t) => t.projectId === p.id)
  const ids = new Set(pts.map((t) => t.id))
  pts.forEach((t) => add(t.assignee))
  subtasks.forEach((s) => { if (ids.has(s.taskId)) (s.assignee ?? '').split(';').forEach((x) => add(x)) })
  return Array.from(seen.values())
}

export default function TimelinePage() {
  const { projects, tasks, subtasks, getEffectiveTaskStatus, updateProject } = useApp()
  const { data: session } = useSession()
  const isHolding = hasCrossDeptView(session?.user ?? {})

  const [dept, setDept] = useState<string | null>(null)
  const [person, setPerson] = useState<string | null>(null)
  const [view, setView] = useState<'week' | 'month'>('week')
  const [openId, setOpenId] = useState<string | null>(null)

  const peopleByProject = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const p of projects) m.set(p.id, peopleOf(p, tasks, subtasks))
    return m
  }, [projects, tasks, subtasks])

  // Persen selesai per project (task done / total; pakai status efektif spt gantt V1).
  const pctByProject = useMemo(() => {
    const m = new Map<string, number>()
    for (const p of projects) {
      const pt = tasks.filter((t) => t.projectId === p.id)
      const done = pt.filter((t) => getEffectiveTaskStatus(t.id) === 'done').length
      m.set(p.id, pt.length > 0 ? Math.round((done / pt.length) * 100) : 0)
    }
    return m
  }, [projects, tasks, getEffectiveTaskStatus])

  const deptScoped = useMemo(
    () => (isHolding && dept ? projects.filter((p) => p.department === dept || p.assignedDepartment === dept) : projects),
    [projects, isHolding, dept],
  )
  const peopleOptions = useMemo(() => {
    const s = new Set<string>()
    for (const p of deptScoped) for (const n of peopleByProject.get(p.id) ?? []) s.add(n)
    return Array.from(s).sort((a, b) => a.localeCompare(b))
  }, [deptScoped, peopleByProject])

  const visible = useMemo(() => {
    let list = [...deptScoped]
    if (person) list = list.filter((p) => (peopleByProject.get(p.id) ?? []).some((n) => n.toLowerCase() === person.toLowerCase()))
    return list.sort((a, b) => {
      const da = a.department ?? '~', db = b.department ?? '~'
      if (da !== db) return da.localeCompare(db)
      const ea = endOf(a), eb = endOf(b)
      if (ea && eb) return ea.getTime() - eb.getTime()
      if (ea) return -1; if (eb) return 1
      return startOf(a).getTime() - startOf(b).getTime()
    })
  }, [deptScoped, person, peopleByProject])

  const range = useMemo(() => {
    const times: number[] = [Date.now()]
    for (const p of visible) { times.push(startOf(p).getTime()); const e = endOf(p); if (e) times.push(e.getTime()) }
    const lo = new Date(Math.min(...times)), hi = new Date(Math.max(...times))
    let start: Date, end: Date
    if (view === 'week') { start = startOfWeek(lo); end = startOfWeek(hi); end.setDate(end.getDate() + 6) }
    else { start = new Date(lo.getFullYear(), lo.getMonth(), 1); end = new Date(hi.getFullYear(), hi.getMonth() + 1, 0) }
    return { start, end, totalDays: Math.round((end.getTime() - start.getTime()) / DAY) + 1 }
  }, [visible, view])

  const columns = useMemo(() => {
    const out: { date: Date; days: number }[] = []
    if (view === 'week') { const n = Math.ceil(range.totalDays / 7); for (let i = 0; i < n; i++) out.push({ date: new Date(range.start.getTime() + i * 7 * DAY), days: 7 }) }
    else { let cur = new Date(range.start); while (cur <= range.end) { out.push({ date: new Date(cur), days: daysInMonth(cur) }); cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1) } }
    return out
  }, [range, view])

  const pxPerDay = view === 'week' ? 9 : 3.4
  const gridWidth = range.totalDays * pxPerDay
  const xFrac = useCallback((d: Date) => Math.max(0, Math.min(1, (d.getTime() - range.start.getTime()) / DAY / range.totalDays)), [range])
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const todayLeft = today >= range.start && today <= range.end ? xFrac(today) * 100 : null

  const groups = useMemo(() => {
    const m = new Map<string, Project[]>()
    for (const p of visible) { const k = p.department ?? '—'; (m.get(k) ?? m.set(k, []).get(k)!).push(p) }
    return Array.from(m.entries())
  }, [visible])

  const openProject = visible.find((p) => p.id === openId) ?? null
  const btn = (on: boolean): CSSProperties => ({ padding: '6px 12px', fontSize: 12, border: 'none', cursor: 'pointer', background: on ? '#1A1916' : '#fff', color: on ? '#fff' : '#6B6860' })

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      <header className="bg-white border-b border-[#E8E7E3] sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-6 flex items-center gap-3" style={{ minHeight: 52 }}>
          <GanttChartSquare className="w-4 h-4 text-[#6B6860]" />
          <span className="font-display text-[15px] font-bold text-[#1A1916]">Timeline</span>
          <span className="text-[11px] text-[#AEADA8] font-body">mulai → deadline · per minggu/bulan</span>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-5">
        <div className="flex gap-2 flex-wrap items-center mb-4">
          <div className="flex rounded-lg border border-[#E8E7E3] overflow-hidden">
            {(['week', 'month'] as const).map((vw) => (
              <button key={vw} onClick={() => setView(vw)} style={btn(view === vw)}>{vw === 'week' ? 'Minggu' : 'Bulan'}</button>
            ))}
          </div>
          <select value={person ?? ''} onChange={(e) => setPerson(e.target.value || null)}
            className="bg-white text-[#1A1916] border border-[#E8E7E3] rounded-lg px-2.5 py-1.5 text-[12px] font-body">
            <option value="">Semua orang</option>
            {peopleOptions.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {isHolding && (
            <div className="flex gap-1.5 flex-wrap">
              <Chip active={dept === null} onClick={() => setDept(null)}>Semua</Chip>
              {DEPARTMENTS.map((d) => <Chip key={d} active={dept === d} onClick={() => setDept(dept === d ? null : d)}>{d}</Chip>)}
            </div>
          )}
        </div>

        {visible.length === 0 ? (
          <div className="bg-white border border-[#E8E7E3] rounded-2xl p-10 text-center text-[#8A8A85] font-body text-sm">{person ? `Tidak ada project untuk ${person}.` : 'Belum ada project.'}</div>
        ) : (
          <div className="bg-white border border-[#E8E7E3] rounded-2xl overflow-hidden">
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: LEFT_W + gridWidth }}>
                <div className="flex border-b border-[#E8E7E3]">
                  <div className="flex-shrink-0 px-4 py-2.5 border-r border-[#E8E7E3]" style={{ width: LEFT_W }}>
                    <span className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body">Project</span>
                  </div>
                  <div className="flex" style={{ width: gridWidth }}>
                    {columns.map((col, i) => {
                      const w = col.date, prev = i > 0 ? columns[i - 1].date : null
                      const monthChanged = i === 0 || (prev !== null && w.getMonth() !== prev.getMonth())
                      return (
                        <div key={i} className="flex-shrink-0 px-1 py-2 border-r border-[#F0EFE9] text-center" style={{ width: col.days * pxPerDay }}>
                          {view === 'week' ? (
                            <>
                              <span className="block text-[8px] font-body font-bold text-[#6366F1] uppercase tracking-wide leading-tight">W{i + 1}</span>
                              <span className="block text-[10px] font-body font-semibold text-[#6B6860] leading-tight">{w.getDate()}</span>
                              <span className="block text-[8px] font-body text-[#C0BDB6] uppercase leading-tight">{monthChanged ? w.toLocaleDateString('id-ID', { month: 'short' }) : ' '}</span>
                            </>
                          ) : (
                            <>
                              <span className="block text-[10px] font-body font-semibold text-[#6B6860] leading-tight">{w.toLocaleDateString('id-ID', { month: 'short' })}</span>
                              <span className="block text-[8px] font-body text-[#C0BDB6] leading-tight">{w.getMonth() === 0 || i === 0 ? `'${String(w.getFullYear()).slice(2)}` : ' '}</span>
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>

                {groups.map(([depName, items]) => {
                  const c = deptColor(depName === '—' ? null : depName)
                  return (
                    <div key={depName}>
                      <div className="flex items-center gap-2 px-4 py-1.5 bg-[#FAFAF8] border-b border-[#F0EFE9]">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-body font-bold uppercase tracking-wider border" style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}>{depName}</span>
                        <span className="text-[10px] text-[#C0BDB6] font-body">{items.length} project</span>
                      </div>
                      {items.map((p) => {
                        const s = startOf(p), e = endOf(p)
                        const left = xFrac(s) * 100
                        const width = e ? Math.max((6 / gridWidth) * 100, (xFrac(e) - xFrac(s)) * 100) : 0
                        const pct = pctByProject.get(p.id) ?? 0
                        const complete = pct >= 100
                        const overdue = !!e && e < today && !complete
                        const barColor = complete ? '#16A34A' : (p.color || '#6366F1')
                        const people = peopleByProject.get(p.id) ?? []
                        return (
                          <button key={p.id} onClick={() => setOpenId(p.id)}
                            className={`flex w-full text-left border-b border-[#F4F3F0] transition-colors ${openId === p.id ? 'bg-[#F4F3FF]' : 'hover:bg-[#FAFAF8]'}`}>
                            <div className="flex-shrink-0 px-4 py-2 border-r border-[#E8E7E3] min-w-0 flex flex-col justify-center gap-1" style={{ width: LEFT_W, minHeight: ROW_H }}>
                              <p className="text-[12.5px] font-body font-semibold text-[#1A1916] truncate">{p.name}</p>
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Avatars people={people} pic={p.pic} />
                                <span className="text-[10px] text-[#AEADA8] font-body truncate">{e ? fmtDay(e) : 'tanpa deadline'}</span>
                              </div>
                            </div>
                            <div className="relative flex-shrink-0" style={{ width: gridWidth, minHeight: ROW_H }}>
                              {todayLeft !== null && <div className="absolute top-0 bottom-0 w-px pointer-events-none" style={{ left: `${todayLeft}%`, background: 'rgba(220,38,38,0.4)' }} />}
                              {e ? (
                                <div title={`${fmtDay(s)} → ${fmtDay(e)} · ${pct}%`}
                                  className={`absolute top-1/2 -translate-y-1/2 h-4 rounded-full overflow-hidden flex items-center ${overdue ? 'ring-1 ring-[#DC2626]' : ''}`}
                                  style={{ left: `${left}%`, width: `${width}%`, backgroundColor: `${barColor}33` }}>
                                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                                  <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[8px] font-body font-bold leading-none px-1 py-px rounded"
                                    style={{ color: barColor, backgroundColor: 'rgba(255,255,255,0.9)' }}>{pct}%</span>
                                </div>
                              ) : (
                                <div title={`Mulai ${fmtDay(s)}`} className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ring-2 ring-white" style={{ left: `${left}%`, background: p.color || '#6366F1' }} />
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 px-4 py-3 border-t border-[#F4F3F0] text-[10px] font-body text-[#8A8A85]">
              <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-full bg-[#16A34A] inline-block" /> 100% selesai</span>
              <span className="flex items-center gap-1.5"><span className="w-0.5 h-3 bg-[rgba(220,38,38,0.4)] inline-block" /> Hari ini</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-full ring-1 ring-[#DC2626] inline-block" /> Telat</span>
            </div>
          </div>
        )}
      </main>

      {openProject && <Drawer project={openProject} tasks={tasks} subtasks={subtasks} authorName={session?.user?.name ?? null} onClose={() => setOpenId(null)} onPatch={updateProject} />}
    </div>
  )
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return <button onClick={onClick} className="px-2.5 py-1 rounded-lg text-[11px] font-body font-medium border transition-colors"
    style={active ? { background: '#1A1916', color: '#fff', borderColor: '#1A1916' } : { background: '#fff', color: '#6B6860', borderColor: '#E8E7E3' }}>{children}</button>
}

function Avatars({ people, pic }: { people: string[]; pic: string | null }) {
  if (!people.length) return <span className="text-[10px] text-[#C0BDB6] font-body">Tanpa PIC</span>
  const shown = people.slice(0, 4)
  const extra = people.length - shown.length
  const picKey = (pic ?? '').trim().toLowerCase()
  return (
    <div className="flex -space-x-1.5 flex-shrink-0">
      {shown.map((n) => (
        <div key={n} title={n.toLowerCase() === picKey ? `${n} (PIC)` : n} className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-body font-bold text-white ring-2 ring-white"
          style={{ backgroundColor: picColor(n), boxShadow: n.toLowerCase() === picKey ? '0 0 0 1.5px #6366F1' : undefined }}>{n.charAt(0).toUpperCase()}</div>
      ))}
      {extra > 0 && <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-body font-bold text-[#6B6860] bg-[#EDECE7] ring-2 ring-white">+{extra}</div>}
    </div>
  )
}

function Drawer({ project, tasks, subtasks, authorName, onClose, onPatch }: {
  project: Project; tasks: Task[]; subtasks: Subtask[]; authorName: string | null
  onClose: () => void; onPatch: (id: string, updates: Partial<Project>) => Promise<void>
}) {
  const [notes, setNotes] = useState<ProjectNote[] | null>(null)
  const [content, setContent] = useState('')
  const [noteDate, setNoteDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [saving, setSaving] = useState(false)
  const people = peopleOf(project, tasks, subtasks)
  const c = deptColor(project.department ?? null)

  useEffect(() => {
    let active = true
    fetch(`/api/projects/${project.id}/notes`).then((r) => r.json())
      .then((d) => { if (active) setNotes(Array.isArray(d) ? d : []) })
      .catch(() => { if (active) setNotes([]) })
    return () => { active = false }
  }, [project.id])

  // Start = created_at, deadline = Deadline. Server menegakkan izin; gagal → rollback + toast (updateProject).
  const patchDate = (field: 'createdAt' | 'deadline', value: string) => {
    void onPatch(project.id, { [field]: value || null } as Partial<Project>)
  }
  const addNote = async () => {
    if (!content.trim() || saving) return
    setSaving(true)
    try {
      const res = await fetch(`/api/projects/${project.id}/notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: content.trim(), author: authorName, noteDate }),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? 'Gagal')
      const n = (await res.json()) as ProjectNote
      setNotes((p) => [n, ...(p ?? [])]); setContent('')
    } catch (e) { alert(e instanceof Error ? e.message : 'Gagal') } finally { setSaving(false) }
  }
  const dateInput = 'w-full text-[12px] font-body text-[#1A1916] bg-[#F8F7F3] border border-[#E0DFD8] rounded-lg px-2.5 py-2 focus:outline-none focus:border-[#6366F1]'

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-[440px] bg-white shadow-2xl flex flex-col">
        <div className="flex items-start justify-between gap-2 px-5 py-4 border-b border-[#F2F1EC] flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: project.color }} />
              <h2 className="font-display text-[15px] font-bold text-[#1A1916] break-words">{project.name}</h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {project.department && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-body font-bold uppercase tracking-wider border" style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}>{project.department}</span>}
              <Avatars people={people} pic={project.pic} />
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#1A1916] transition-colors flex-shrink-0" aria-label="Tutup">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-5 py-4 border-b border-[#F2F1EC] grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-[#C0BDB6] font-body mb-1 block">Mulai</span>
              <input type="date" defaultValue={isoDay(project.createdAt)} onChange={(e) => patchDate('createdAt', e.target.value)} className={dateInput} />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-[#C0BDB6] font-body mb-1 block">Deadline</span>
              <input type="date" defaultValue={isoDay(project.deadline)} onChange={(e) => patchDate('deadline', e.target.value)} className={dateInput} />
            </label>
            <p className="col-span-2 text-[10px] text-[#AEADA8] font-body">Hanya pemegang (PIC), pemilik, atau admin yang bisa mengubah tanggal.</p>
          </div>

          <div className="px-5 py-4">
            <p className="text-[11px] uppercase tracking-wider text-[#6B6860] font-body font-semibold mb-3">Komentar</p>
            <div className="mb-4 rounded-xl border border-[#E8E7E3] p-3 bg-[#FAFAF8]">
              <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={2} placeholder="Tulis komentar untuk tanggal tertentu…"
                className="w-full text-[12.5px] font-body text-[#1A1916] bg-white border border-[#E0DFD8] rounded-lg px-2.5 py-2 resize-none focus:outline-none focus:border-[#6366F1] placeholder:text-[#C0BDB6]" />
              <div className="flex items-center gap-2 mt-2">
                <input type="date" value={noteDate} onChange={(e) => setNoteDate(e.target.value)} className="text-[11px] font-body text-[#1A1916] bg-white border border-[#E0DFD8] rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#6366F1]" />
                <button onClick={addNote} disabled={!content.trim() || saving} className="ml-auto px-3 py-1.5 rounded-lg bg-[#1A1916] text-white text-[12px] font-body font-medium hover:bg-[#2E2E2A] disabled:opacity-40 transition-colors">{saving ? 'Menyimpan…' : 'Tambah'}</button>
              </div>
            </div>
            {notes === null ? <p className="text-[12px] text-[#AEADA8] font-body py-2">Memuat…</p>
              : notes.length === 0 ? <p className="text-[12px] text-[#AEADA8] font-body py-2">Belum ada komentar.</p>
              : (
                <ul className="space-y-2.5">
                  {notes.map((n) => (
                    <li key={n.id} className="rounded-xl border border-[#F0EFE9] p-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="inline-flex items-center gap-1 text-[10px] font-body font-semibold text-[#4338CA] bg-[#EEF2FF] border border-[#C7D2FE] px-1.5 py-0.5 rounded-md">{fmtDay(new Date(n.noteDate))}</span>
                        {n.author && <span className="text-[10px] text-[#8A8A85] font-body">{n.author}</span>}
                      </div>
                      <p className="text-[12.5px] font-body text-[#3A3A36] whitespace-pre-wrap break-words leading-relaxed">{n.content}</p>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>
      </div>
    </div>
  )
}
